import { useState } from 'react';
import { useSearchParams, Link, useNavigate } from 'react-router-dom';

export default function ResetPasswordPage() {
  const [searchParams] = useSearchParams();
  const tokenFromUrl = searchParams.get('token') || '';
  const navigate = useNavigate();

  const [token, setToken] = useState(tokenFromUrl);
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');

    if (!token) {
      setError('Reset token is required');
      return;
    }

    if (newPassword.length < 8) {
      setError('Password must be at least 8 characters');
      return;
    }

    if (newPassword !== confirmPassword) {
      setError('Passwords do not match');
      return;
    }

    setLoading(true);

    try {
      const resp = await fetch('/api/auth/reset-password', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ token, newPassword }),
      });

      const data = await resp.json();

      if (!resp.ok) {
        throw new Error(data.error || 'Failed to reset password');
      }

      setSuccess(true);
      setTimeout(() => navigate('/login'), 2500);
    } catch (err: any) {
      setError(err.message || 'Failed to reset password');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center px-4 py-12">
      <div className="max-w-md w-full">
        <div className="text-center mb-8">
          <h1 className="text-3xl font-black uppercase tracking-widest text-navy-900 dark:text-cream-50 mb-2">
            New Password
          </h1>
          <p className="text-navy-400 dark:text-navy-300 text-sm">
            Set a new password for your account
          </p>
        </div>

        <div className="glass-panel rounded-lg shadow-xl p-8">
          {error && (
            <div className="bg-red-500/10 border border-red-500 text-red-600 dark:text-red-400 px-4 py-3 rounded mb-4 text-sm">
              {error}
            </div>
          )}

          {success ? (
            <div className="text-center">
              <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-green-100 dark:bg-green-900/30 flex items-center justify-center">
                <svg
                  className="w-8 h-8 text-green-600 dark:text-green-400"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M5 13l4 4L19 7"
                  />
                </svg>
              </div>
              <p className="text-navy-900 dark:text-cream-50 font-bold mb-2">
                Password reset successfully
              </p>
              <p className="text-navy-500 dark:text-cream-300 text-sm">
                Redirecting to login...
              </p>
            </div>
          ) : (
            <form onSubmit={handleSubmit}>
              <div className="mb-4">
                <label className="block text-navy-700 dark:text-cream-200 text-sm font-black uppercase tracking-widest mb-2">
                  Reset Token
                </label>
                <input
                  type="text"
                  value={token}
                  onChange={(e) => setToken(e.target.value)}
                  className="w-full bg-cream-50 dark:bg-navy-900 text-navy-900 dark:text-cream-50 border border-cream-300 dark:border-navy-600 px-4 py-3 rounded-lg focus:outline-none focus:ring-2 focus:ring-gold-500 mono text-xs"
                  required
                  disabled={loading}
                />
              </div>

              <div className="mb-4">
                <label className="block text-navy-700 dark:text-cream-200 text-sm font-black uppercase tracking-widest mb-2">
                  New Password
                </label>
                <input
                  type="password"
                  value={newPassword}
                  onChange={(e) => setNewPassword(e.target.value)}
                  className="w-full bg-cream-50 dark:bg-navy-900 text-navy-900 dark:text-cream-50 border border-cream-300 dark:border-navy-600 px-4 py-3 rounded-lg focus:outline-none focus:ring-2 focus:ring-gold-500"
                  required
                  disabled={loading}
                  minLength={8}
                />
                <p className="text-xs text-navy-400 dark:text-navy-300 mt-1">
                  Minimum 8 characters
                </p>
              </div>

              <div className="mb-6">
                <label className="block text-navy-700 dark:text-cream-200 text-sm font-black uppercase tracking-widest mb-2">
                  Confirm Password
                </label>
                <input
                  type="password"
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  className="w-full bg-cream-50 dark:bg-navy-900 text-navy-900 dark:text-cream-50 border border-cream-300 dark:border-navy-600 px-4 py-3 rounded-lg focus:outline-none focus:ring-2 focus:ring-gold-500"
                  required
                  disabled={loading}
                />
              </div>

              <button
                type="submit"
                disabled={loading}
                className="w-full bg-gold-500 hover:bg-gold-600 active:bg-gold-700 text-navy-900 font-black uppercase tracking-widest text-sm py-3 px-4 rounded-lg transition disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {loading ? 'Resetting...' : 'Reset Password'}
              </button>
            </form>
          )}

          <div className="mt-6 text-center">
            <Link
              to="/login"
              className="text-navy-500 dark:text-cream-300 hover:text-gold-600 dark:hover:text-gold-400 text-sm font-bold uppercase tracking-widest transition"
            >
              Back to Login
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
}
